import { SelectValue, LabeledValue } from 'antd/lib/select';
import React, { FocusEvent, useCallback, useEffect, useState } from 'react';
import { GroupWidgetPort } from '@kemu-io/kemu-core/types';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons';
import classNames from 'classnames';
import { DataType } from '@kemu-io/hs-types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import styles from './portsList.module.css';
import SortablePortRow from './SortablePortRow/SortablePortRow';
import StyledLabel from '@components/form-control/styledLabel/styledLabel';
import IconButton from '@components/form-control/iconButton/iconButton';
import StyledSelect, { Option } from '@components/form-control/styledSelect/styledSelect';
import StyledInput from '@components/form-control/styledInput/styledInput';
import useTranslation from '@hooks/useTranslation';
import StyledButton from '@components/form-control/styledButton/styledButton';
import { portTypeToString } from '@common/utils';

interface Props {
	ports: GroupWidgetPort[];
	onAddPort: () => void;
	onRemovePort: (index: number) => void;
	onPortUpdated: (index: number, port: GroupWidgetPort) => void;
	onReorder?: (reorderedPorts: GroupWidgetPort[]) => void;
	'data-kemu-meta'?: string;
}


const PortsList = (props: Props): React.JSX.Element => {
	const t = useTranslation('Interface.Modals.CreateWidget.Ports');
	const typeT = useTranslation('Kemu.DataType.HumanReadable');
	const { onPortUpdated, ports: propPorts, onReorder } = props;
	const [ports, setPorts] = useState<GroupWidgetPort[]>(propPorts);
	const sensors = useSensors(
		useSensor(PointerSensor),
	);

	const onPortTypeUpdated = useCallback((value: SelectValue, options: LabeledValue[], portId?: string | number) => {
		const index = portId as number;
		const keys = options.map(item => (!isNaN(Number(item.key)) ? Number(item.key) : DataType.Anything) as DataType);
		const portInfo = ports[index];

		onPortUpdated?.(index, { ...portInfo, type: keys });
	}, [onPortUpdated, ports]);

	const handlePortNameChange = (text: string, originalEvent: FocusEvent<HTMLInputElement>, elementId?: string | number) => {
		const index = elementId as number;
		const trimmedText = text.trim();
		const port = props.ports[index];

		// If empty on blur, fall back to port.name
		if (trimmedText === '') {
			props.onPortUpdated(index, { ...port, label: port.name });
			return;
		}

		// Check if name exists in other ports (excluding current port)
		const nameExists = props.ports.find((p, i) => i !== index && p.label === trimmedText);
		if (port.label !== text) {
			if (!nameExists) {
				props.onPortUpdated(index, { ...port, label: text });
			} else {
				// Restore original value only if duplicate name exists
				originalEvent.currentTarget.value = port.label || port.name || '';
			}
		}
	};

	const handleNameChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>, elementId?: string | number) => {
		const index = elementId as number;
		const name = evt.target.value;
		setPorts(list => {
			if (list[index].label !== name) {
				const updatedPorts = list.map(port => ({ ...port }));
				updatedPorts[index].label = name;
				return updatedPorts;
			}

			return list;
		});
	}, []);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const oldIndex = ports.findIndex((port) => port.name === active.id);
			const newIndex = ports.findIndex((port) => port.name === over?.id);

			if (oldIndex !== -1 && newIndex !== -1) {
				const reorderedPorts = arrayMove(ports, oldIndex, newIndex);

				// Update indices for all reordered ports
				const portsWithUpdatedIndices = reorderedPorts.map((port, index) => ({
					...port,
					index,
				}));

				setPorts(portsWithUpdatedIndices);
				onReorder?.(portsWithUpdatedIndices);
			}
		}
	}, [ports, onReorder]);

	useEffect(() => {
		setPorts(props.ports);
	}, [props.ports]);


	return (
		<div className={styles.PortsContainer} data-kemu-meta={props['data-kemu-meta'] || 'ports-list'}>
			<div className={styles.Headers}>
				<StyledLabel text={t('Header.Name', 'Name')} />
				<StyledLabel text={t('Header.DataType', 'Data Type')} />
			</div>

			{ports.length === 0 && (
				<StyledButton
					className={styles.PortActionBtn}
					color="light"
					title={t('AddPortBtn', 'Add Port')}
					onClick={props.onAddPort}
				/>
			)}

			{ports.length > 0 && (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext items={ports.map((port) => port.name)} strategy={verticalListSortingStrategy}>
						{ports.map((port, i) => (
							<SortablePortRow
								key={port.name}
								port={port}
								index={i}
								isLast={i === ports.length - 1}
								onPortUpdated={onPortUpdated}
								onRemovePort={props.onRemovePort}
								onAddPort={props.onAddPort}
								onPortNameChange={handlePortNameChange}
								onPortTypeUpdated={onPortTypeUpdated}
								handleNameChange={handleNameChange}
							/>
						))}
					</SortableContext>
				</DndContext>
			)}
		</div>
	);
};

export default PortsList;
